import { ResourceModel } from '@bryntum/schedulerpro';
import { Dictionary } from 'lodash';
import { duration } from 'moment';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { Role, Team, User } from 'api/models';
import { Project } from 'api/project';
import { ResourceType } from 'api/resource-management/types';
import { INVALID_ROLE_ID } from 'components/organisms/resource-management/utils/common';
import {
  resourceIdForAssignProject,
  resourceIdForAssignUserOrTeam,
  resourceIdForExpandableRole,
  resourceIdForProject,
  resourceIdForProjectAsLeaf,
  resourceIdForRole,
  resourceIdForTeamAsLeaf,
  resourceIdForTeamHeader,
  resourceIdForUser,
  resourceIdForUserAsLeaf,
  resourceIdForCompany,
} from 'components/organisms/resource-management/utils/id';
import {
  expandableRoleAsResource,
  projectAsLeafResource,
  projectAsResource,
  roleAsResource,
  teamAsLeafResource,
  teamAsResource,
  teamsWithProjectGroupedByRoleAsResource,
  userAsLeafResource,
  userAsResource,
} from 'components/organisms/resource-management/utils/resource-tree';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useUser } from 'hooks/use-user';
import { getTeamsInAccountQueryKey } from 'queries/placeholder';
import { fullName } from 'utils/full-name';
import { queueMacrotask } from 'utils/macrotask';
import { sortByText } from 'utils/sort-by-text';
import {
  AddResourceOptions,
  AddResourceProps,
  ExtendedResourceIdentifier,
  ExtendedTeamIdentifier,
  ExtendedUserIdentifier,
  GroupBy,
  SchedulerReference,
} from '../../types';

interface Props {
  groupBy: GroupBy;
  schedulerRef: SchedulerReference;
  roleLookup: Dictionary<Role>;
  projectLookup: Dictionary<Project>;
}

interface Result {
  addResource: (props: AddResourceProps) => ResourceModel | undefined;
  addUserResourceToProject: (
    projectId: number,
    userResource: ExtendedUserIdentifier,
  ) => ResourceModel | undefined;
  addUserResource: (
    userResource: ExtendedUserIdentifier,
    projectsToAdd: Project[],
    isCollaborator: boolean,
  ) => ResourceModel | undefined;
  removeResource: (
    projectId: number,
    resource: ExtendedResourceIdentifier,
  ) => ResourceModel | undefined;
  addRoleResource: (
    resourceType: ResourceType,
    projectId: number,
    role: Role,
    entitiesToAdd: User[] | Team[],
    referenceTeam?: Team,
  ) => ResourceModel;
  addProjectResourceToUserByProject: (
    projectId: Project,
    userResource: ExtendedUserIdentifier,
  ) => ResourceModel | undefined;
  addTeamResourceToRoleWithProject: (
    project: Project,
    team: Team,
    options: AddResourceOptions,
  ) => ResourceModel | undefined;
}

export const useAddRemoveResource = ({
  groupBy,
  schedulerRef,
  roleLookup,
  projectLookup,
}: Props): Result => {
  const queryClient = useQueryClient();
  const isCollaborator = useIsCurrentUserCollaborator();
  const {
    user: { userId: currentUserId },
  } = useUser();

  const highlightResourceRow = useCallback(
    (
      resource: ResourceModel,
      highlightFor = duration(2, 'seconds').asMilliseconds(),
    ) => {
      schedulerRef.current.instance.scrollRowIntoView(resource);
      const row = schedulerRef.current.instance.getRowFor(resource);

      if (row) {
        row.cls.highlight = true;

        window.setTimeout(() => {
          if (schedulerRef.current) {
            schedulerRef.current.instance.suspendRefresh();
            row.cls.highlight = false;
            schedulerRef.current.instance.resumeRefresh(true);
          }
        }, highlightFor);
      }
    },
    [schedulerRef],
  );
  const addRoleResource = useCallback(
    (
      resourceType: ResourceType,
      projectId: number,
      role: Role,
      entitiesToAdd: User[] | Team[],
      referenceTeam?: Team,
    ): ResourceModel => {
      const projectResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForProject(projectId),
        );
      const newResource = roleAsResource(projectId, role);

      entitiesToAdd.forEach((entity) =>
        newResource.children.push(
          resourceType === ResourceType.User
            ? userAsLeafResource({
                projectId,
                resource: {
                  type: resourceType,
                  user: entity,
                  role: referenceTeam ? role : undefined,
                  referenceTeam,
                },
                totalAllocation: 0,
                autoAllocation: projectResource?.get('autoAllocation') ?? false,
              })
            : teamAsLeafResource(
                entity,
                projectId,
                undefined,
                projectResource?.get('autoAllocation') ?? false,
              ),
        ),
      );
      const parentResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForProject(projectId),
        );

      if (!parentResource.children) {
        return parentResource.insertChild(newResource);
      }
      const beforeElement =
        parentResource.children.find(
          (r) => sortByText(r.get('roleName'), role.roleName) === 1,
        ) ??
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForAssignUserOrTeam(projectId),
        );

      return parentResource.insertChild(newResource, beforeElement);
    },
    [schedulerRef],
  );
  const addUserResourceToProject = useCallback(
    (
      projectId: number,
      userResource: ExtendedUserIdentifier,
    ): ResourceModel | undefined => {
      const { user, role: memberRole, referenceTeam } = userResource;
      const { userId, role } = user;
      const projectResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForProject(projectId),
        );
      const existingUserResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForUserAsLeaf({
            projectId,
            userId,
            roleId: memberRole?.roleId,
            referenceTeamId: referenceTeam?.teamId,
          }),
        );

      if (existingUserResource != null) {
        return undefined;
      }

      const roleToUse = referenceTeam ? memberRole : role;

      if (roleToUse == null) {
        return undefined;
      }

      const parentResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForRole(projectId, roleToUse.roleId),
        );

      if (parentResource != null) {
        const newResource = userAsLeafResource({
          projectId,
          resource: userResource,
          totalAllocation: 0,
          autoAllocation: projectResource?.get('autoAllocation') ?? false,
        });
        const beforeElement = parentResource.children.find(
          (resource) =>
            sortByText(
              fullName({
                firstName: resource.get('firstName'),
                lastName: resource.get('lastName'),
              }),
              fullName(user),
            ) === 1,
        );
        const newlyAdded = parentResource.insertChild(
          newResource,
          beforeElement,
        );

        return newlyAdded;
      }
      const newlyAdded = addRoleResource(
        ResourceType.User,
        projectId,
        roleToUse,
        [user],
        referenceTeam,
      );

      schedulerRef.current.instance.expand(newlyAdded.id);

      return newlyAdded;
    },
    [addRoleResource, schedulerRef],
  );
  const addUserResource = useCallback(
    (
      userResource: ExtendedUserIdentifier,
      projectsToAdd: Project[],
    ): ResourceModel | undefined => {
      const { user } = userResource;

      if (
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForUser(user.userId),
        ) != null
      ) {
        return undefined;
      }

      const companyResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForCompany(user.company?.companyId),
        );

      const newResource = userAsResource(
        {
          user,
          projects: projectsToAdd.map((project) => ({ project })),
        },
        {
          includeAssignProject: !isCollaborator,
        },
      );

      if (companyResource == null) {
        const [addedResource] =
          schedulerRef.current.instance.resourceStore.add(newResource);

        return addedResource;
      }
      const beforeElement = companyResource.children.find(
        (resource) =>
          sortByText(
            fullName({
              firstName: resource.get('firstName'),
              lastName: resource.get('lastName'),
            }),
            fullName(user),
          ) === 1,
      );
      const newlyAdded =
        beforeElement != null
          ? companyResource.insertChild(newResource, beforeElement)
          : companyResource.appendChild(newResource);

      return newlyAdded;
    },
    [isCollaborator, schedulerRef],
  );
  const addTeamResourceToProject = useCallback(
    (projectId: number, team: Team): ResourceModel | undefined => {
      const { teamId, teamName, roleId } = team;
      const projectResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForProject(projectId),
        );

      const existingTeamResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamAsLeaf(projectId, teamId),
        );

      if (
        existingTeamResource != null ||
        roleId == null ||
        roleId === INVALID_ROLE_ID
      ) {
        return undefined;
      }

      const parentResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForRole(projectId, roleId),
        );

      if (parentResource != null) {
        const newResource = teamAsLeafResource(
          team,
          projectId,
          undefined,
          projectResource?.get('autoAllocation') ?? false,
        );
        const beforeElement = parentResource.children.find(
          (resource) =>
            sortByText(
              resource.get('teamName') ?? fullName(resource),
              teamName,
            ) === 1,
        );
        const newlyAdded = parentResource.insertChild(
          newResource,
          beforeElement,
        );

        return newlyAdded;
      }
      const newlyAdded = addRoleResource(
        ResourceType.Team,
        projectId,
        roleLookup[roleId],
        [team],
      );

      schedulerRef.current.instance.expand(newlyAdded.id);

      return newlyAdded;
    },
    [addRoleResource, schedulerRef, roleLookup],
  );
  const addProjectResourceToUserByProject = useCallback(
    (
      project: Project,
      userResource: ExtendedUserIdentifier,
    ): ResourceModel | undefined => {
      const {
        user: { userId },
        role: memberRole,
        referenceTeam,
      } = userResource;

      const existingResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForProjectAsLeaf({
            projectId: project.projectId,
            userId,
            roleId: memberRole?.roleId,
            referenceTeamId: referenceTeam?.teamId,
          }),
        );

      if (existingResource != null) {
        return undefined;
      }
      const parentResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForUser(userId),
        );
      const newResource = projectAsLeafResource({
        project,
        userId,
        roleId: memberRole?.roleId,
        referenceTeam,
      });
      const beforeElement = parentResource.children.find(
        (resource) =>
          resource.get('projectName') == null ||
          sortByText(resource.get('projectName'), project.projectName) === 1,
      );

      schedulerRef.current.instance.suspendRefresh();
      const newlyAdded = parentResource.insertChild(newResource, beforeElement);

      const assignProject = schedulerRef.current.instance.resourceStore.getById(
        resourceIdForAssignProject(ResourceType.User, userId),
      );

      if (assignProject) {
        assignProject.set('alreadyAssignedProjectIds', [
          ...assignProject.get('alreadyAssignedProjectIds'),
          project.projectId,
        ]);
      }
      schedulerRef.current.instance.resumeRefresh(true);

      return newlyAdded;
    },
    [schedulerRef],
  );
  const addProjectResourceToUser = useCallback(
    (
      projectId: number,
      userResource: ExtendedUserIdentifier,
    ): ResourceModel | undefined => {
      return addProjectResourceToUserByProject(
        projectLookup[projectId],
        userResource,
      );
    },
    [addProjectResourceToUserByProject, projectLookup],
  );

  const addExpandableRoleResource = useCallback(
    (
      project: Project,
      team: Team,
      roleId: number,
      options: AddResourceOptions,
    ): ResourceModel => {
      const parentResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamHeader(),
        );

      const teamWithProjectGroupedByRoleLookup = {
        [roleId]: [
          {
            ...team,
            ...project,
          },
        ],
      };

      if (parentResource != null) {
        const newResource = expandableRoleAsResource(
          roleLookup[roleId],
          teamWithProjectGroupedByRoleLookup,
        );
        // TODO: Check and remove this logic as sorting is handled

        schedulerRef.current.instance.suspendRefresh();
        const newlyAdded = parentResource.appendChild(newResource);

        if (options.expand === true) {
          schedulerRef.current.instance.expand(newlyAdded.id);
        }

        schedulerRef.current.instance.resumeRefresh(true);

        return newlyAdded;
      }

      const [newlyAdded] = schedulerRef.current.instance.resourceStore.insert(
        0,
        teamsWithProjectGroupedByRoleAsResource(
          teamWithProjectGroupedByRoleLookup,
          roleLookup,
        ),
      );

      if (options.expand === true) {
        schedulerRef.current.instance.expand(resourceIdForTeamHeader());
        schedulerRef.current.instance.expand(
          resourceIdForExpandableRole(roleId),
        );
      }

      return newlyAdded;
    },
    [roleLookup, schedulerRef],
  );
  const addTeamResourceToRoleWithProject = useCallback(
    (
      project: Project,
      team: Team,
      options: AddResourceOptions,
    ): ResourceModel | undefined => {
      const { teamId, roleId, teamName } = team;
      const existingResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamAsLeaf(project.projectId, teamId),
        );

      if (
        existingResource != null ||
        roleId == null ||
        roleId === INVALID_ROLE_ID
      ) {
        return undefined;
      }
      const parentResource =
        schedulerRef.current.instance.resourceStore.getById(
          resourceIdForExpandableRole(roleId),
        );

      if (parentResource != null) {
        const newResource = teamAsResource({
          ...project,
          ...team,
        });
        const beforeElement = parentResource.children.find(
          (resource) => sortByText(resource.get('teamName'), teamName) === 1,
        );

        schedulerRef.current.instance.suspendRefresh();
        const newlyAdded =
          beforeElement != null
            ? parentResource.insertChild(newResource, beforeElement)
            : parentResource.appendChild(newResource);

        parentResource.set('teamCount', parentResource.get('teamCount') + 1);
        if (options.expand === true) {
          schedulerRef.current.instance.expand(resourceIdForTeamHeader());
          schedulerRef.current.instance.expand(
            resourceIdForExpandableRole(roleId),
          );
        }
        schedulerRef.current.instance.resumeRefresh(true);

        return newlyAdded;
      }

      return addExpandableRoleResource(project, team, roleId, options);
    },
    [addExpandableRoleResource, schedulerRef],
  );
  const addTeamResourceToRole = useCallback(
    (
      projectId: number,
      team: Team,
      options: AddResourceOptions,
    ): ResourceModel | undefined => {
      return addTeamResourceToRoleWithProject(
        projectLookup[projectId],
        team,
        options,
      );
    },
    [addTeamResourceToRoleWithProject, projectLookup],
  );
  const addResource = useCallback(
    ({
      projectId,
      resource,
      options = {
        highlight: true,
        expand: true,
      },
    }: AddResourceProps): ResourceModel | undefined => {
      const teams = queryClient.getQueryData<Team[]>(
        getTeamsInAccountQueryKey(),
      );

      if (groupBy === GroupBy.Users) {
        let newlyAdded;

        if (resource.type === ResourceType.User) {
          newlyAdded = addProjectResourceToUser(projectId, {
            ...resource,
            ...(resource.referenceTeam
              ? {
                  referenceTeam: (teams ?? []).find(
                    ({ teamId }) => resource.referenceTeam?.teamId === teamId,
                  ),
                }
              : {}),
          });
        } else if (resource.type === ResourceType.Team) {
          newlyAdded = addTeamResourceToRole(projectId, resource.team, options);
        }

        // TODO: Test in People tab via socket
        if (options.highlight === true) {
          queueMacrotask(() => {
            highlightResourceRow(
              schedulerRef.current.instance.resourceStore.getById(
                resource.type === ResourceType.User
                  ? schedulerRef.current.instance.resourceStore.getById(
                      resourceIdForProjectAsLeaf({
                        projectId,
                        userId: resource.user.userId,
                        roleId: resource.role?.roleId,
                        referenceTeamId: resource.referenceTeam?.teamId,
                      }),
                    )
                  : resource.type === ResourceType.Team
                  ? resourceIdForTeamAsLeaf(projectId, resource.team.teamId)
                  : undefined,
              ),
            );
          });
        }

        return newlyAdded;
      }

      if (resource != null) {
        let newlyAdded;
        const projectResourceId = resourceIdForProject(projectId);
        const projectResource =
          schedulerRef.current.instance.resourceStore.getById(
            projectResourceId,
          );

        if (!projectResource) {
          const newResource = projectAsResource(projectLookup[projectId]);

          schedulerRef.current.instance.resourceStore.add(newResource);
        }
        if (resource.type === ResourceType.User) {
          const shouldAddUser = isCollaborator
            ? resource.user.userId === currentUserId
            : true;

          if (shouldAddUser) {
            newlyAdded = addUserResourceToProject(projectId, {
              ...resource,
              ...(resource.referenceTeam
                ? {
                    referenceTeam: (teams ?? []).find(
                      ({ teamId }) => resource.referenceTeam?.teamId === teamId,
                    ),
                  }
                : {}),
            });
          }
        } else if (resource.type === ResourceType.Team) {
          // ! teamLookup is not updating here when new placeholder is created, hence getting entity directly
          if (!isCollaborator) {
            newlyAdded = addTeamResourceToProject(projectId, resource.team);
          }
        }

        if (options.highlight === true) {
          queueMacrotask(() => {
            highlightResourceRow(
              schedulerRef.current.instance.resourceStore.getById(
                resource.type === ResourceType.User
                  ? resourceIdForUserAsLeaf({
                      projectId,
                      userId: resource.user.userId,
                      roleId: resource.role?.roleId,
                      referenceTeamId: resource.referenceTeam?.teamId,
                    })
                  : resource.type === ResourceType.Team
                  ? resourceIdForTeamAsLeaf(projectId, resource.team.teamId)
                  : undefined,
              ),
            );
          });
        }

        return newlyAdded;
      }

      return undefined;
    },
    [
      queryClient,
      groupBy,
      addProjectResourceToUser,
      addTeamResourceToRole,
      highlightResourceRow,
      schedulerRef,
      projectLookup,
      isCollaborator,
      currentUserId,
      addUserResourceToProject,
      addTeamResourceToProject,
    ],
  );
  const removeProjectResourceIfEmpty = useCallback(
    (projectId) => {
      const projectResourceId = resourceIdForProject(projectId);
      const projectResource =
        schedulerRef.current.instance.resourceStore.getById(projectResourceId);

      if (
        projectResource &&
        (projectResource.unfilteredChildren ?? projectResource.children)
          .length === 0
      ) {
        schedulerRef.current.instance.resourceStore.remove(projectResource);
      }
    },
    [schedulerRef],
  );
  const removeUserResource = useCallback(
    (
      projectId: number,
      userResource: ExtendedUserIdentifier,
    ): ResourceModel | undefined => {
      const {
        user: { userId, role },
        role: memberRole,
        referenceTeam,
      } = userResource;
      const resourceId =
        groupBy === GroupBy.Users
          ? resourceIdForProjectAsLeaf({
              projectId,
              userId,
              roleId: memberRole?.roleId,
              referenceTeamId: referenceTeam?.teamId,
            })
          : resourceIdForUserAsLeaf({
              projectId,
              userId,
              roleId: memberRole?.roleId,
              referenceTeamId: referenceTeam?.teamId,
            });

      const resource =
        schedulerRef.current.instance.resourceStore.getById(resourceId);

      if (resource == null) {
        return;
      }
      schedulerRef.current.instance.suspendRefresh();
      schedulerRef.current.instance.resourceStore.remove(resource);
      if (groupBy === GroupBy.Projects) {
        const roleIdToUse = referenceTeam ? memberRole?.roleId : role?.roleId;

        if (roleIdToUse == null) {
          return;
        }
        const parentResourceId = resourceIdForRole(projectId, roleIdToUse);
        const parentResource =
          schedulerRef.current.instance.resourceStore.getById(parentResourceId);

        if (
          parentResource &&
          (parentResource.unfilteredChildren ?? parentResource.children)
            .length === 0
        ) {
          schedulerRef.current.instance.resourceStore.remove(parentResource);
          if (isCollaborator) {
            removeProjectResourceIfEmpty(projectId);
          }
        }
      }
      if (groupBy === GroupBy.Users) {
        const assignProject =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForAssignProject(ResourceType.User, userId),
          );

        if (assignProject) {
          assignProject.set('alreadyAssignedProjectIds', [
            ...assignProject.get('alreadyAssignedProjectIds'),
            projectId,
          ]);
          assignProject.set(
            'alreadyAssignedProjectIds',
            assignProject
              .get('alreadyAssignedProjectIds')
              .filter((id) => id !== projectId),
          );
        }
      }
      schedulerRef.current.instance.resumeRefresh(true);
    },
    [groupBy, isCollaborator, removeProjectResourceIfEmpty, schedulerRef],
  );
  const removeTeamResource = useCallback(
    (
      projectId: number,
      teamResource: ExtendedTeamIdentifier,
    ): ResourceModel | undefined => {
      const {
        team: { teamId },
      } = teamResource;
      const resourceId = resourceIdForTeamAsLeaf(projectId, teamId);
      const resource =
        schedulerRef.current.instance.resourceStore.getById(resourceId);

      if (resource == null) {
        return;
      }

      const roleId = resource.parent.get('roleId');
      const companyId = resource.parent.get('companyId');

      schedulerRef.current.instance.suspendRefresh();
      schedulerRef.current.instance.resourceStore.remove(resource);

      if (groupBy === GroupBy.Projects) {
        const parentResourceId = resourceIdForRole(projectId, roleId);
        const parentResource =
          schedulerRef.current.instance.resourceStore.getById(parentResourceId);

        if (
          parentResource &&
          (parentResource.unfilteredChildren ?? parentResource.children)
            ?.length === 0
        ) {
          schedulerRef.current.instance.resourceStore.remove(parentResource);
        }
      }
      if (groupBy === GroupBy.Users) {
        const roleResourceId = resourceIdForExpandableRole(roleId, companyId);
        const roleResource =
          schedulerRef.current.instance.resourceStore.getById(roleResourceId);

        if (
          roleResource &&
          (roleResource.unfilteredChildren ?? roleResource.children).length ===
            0
        ) {
          schedulerRef.current.instance.resourceStore.remove(roleResource);
        } else {
          roleResource.set('teamCount', roleResource.get('teamCount') - 1);
        }

        const teamHeaderId = resourceIdForTeamHeader();
        const teamHeader =
          schedulerRef.current.instance.resourceStore.getById(teamHeaderId);

        if (
          teamHeader &&
          (teamHeader.unfilteredChildren ?? teamHeader.children).length === 0
        ) {
          schedulerRef.current.instance.resourceStore.remove(teamHeader);
        }
      }

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [groupBy, schedulerRef],
  );
  const removeResource = useCallback(
    (
      projectId: number,
      resource: ExtendedResourceIdentifier,
    ): ResourceModel | undefined => {
      if (resource.type === ResourceType.User) {
        return removeUserResource(projectId, resource);
      }
      if (resource.type === ResourceType.Team) {
        return removeTeamResource(projectId, resource);
      }

      return undefined;
    },
    [removeTeamResource, removeUserResource],
  );

  return {
    addResource,
    removeResource,
    addUserResourceToProject,
    addProjectResourceToUserByProject,
    addUserResource,
    addRoleResource,
    addTeamResourceToRoleWithProject,
  };
};
